import { lazy } from "react";
import { Redirect } from "react-router-dom";
import {
  PROJECT,
  CONTACT,
  CONTRACT,
  CREATE,
  EDIT,
  ACCOUNT,
  ACCESS,
} from "./utils/constants";

const Notice = lazy(() => import("./pages/notification"));
const Contacts = lazy(() => import("./pages/contacts"));
const Dashboard = lazy(() => import("./pages/dashboard"));
const Projects = lazy(() => import("./pages/projects"));
const UserDetail = lazy(() => import("./pages/manageaccount/pages/profile"));
const EditProfileDetail = lazy(
  () => import("./pages/manageaccount/pages/profile/edit"),
);
const NewContract = lazy(() => import("./pages/contract/create"));
const BaseContract = lazy(() => import("./pages/basecontract"));
const Materials = lazy(() => import("./pages/materials"));
const Variations = lazy(() => import("./pages/variations"));
const ManageAccount = lazy(() => import("./pages/manageaccount/edit"));
const Account = lazy(() => import("./pages/manageaccount/view"));
const ManageUsers = lazy(() => import("./pages/manageaccount/pages/users"));
const XeroSettings = lazy(
  () => import("./pages/manageaccount/pages/xeroSettings"),
);
const ManagePlan = lazy(() => import("./pages/manageaccount/pages/plan"));
const AddUsers = lazy(() => import("./pages/manageaccount/pages/users/add"));
const PlanPrice = lazy(
  () => import("./pages/manageaccount/pages/plan/planPrice"),
);
const EmailSummary = lazy(
  () => import("./pages/manageaccount/pages/emailSummary"),
);
const RoleSelection = lazy(
  () => import("./pages/manageaccount/pages/roleSelection"),
);
const UpdateContact = lazy(() => import("./pages/contacts/pages/editContact"));
const CreateContact = lazy(
  () => import("./pages/contacts/pages/createContact"),
);
const MakeAClaim = lazy(() => import("./pages/makeclaim/makeClaim"));
const ClaimOverview = lazy(() => import("./pages/makeclaim/reviewClaim"));
const MakeACert = lazy(() => import("./pages/makecert/makeCert"));
const CertOverview = lazy(() => import("./pages/makecert/reviewCert"));
const InvitationInfo = lazy(() => import("./pages/invitation/index"));
const ClaimCert = lazy(() => import("./pages/claimsCerts"));
const NewProject = lazy(() => import("./pages/project/create"));
const ProjectInfo = lazy(() => import("./pages/project/view"));
const ProjectSetting = lazy(() => import("./pages/project/update"));
const WorkflowSetting = lazy(() => import("./pages/workflow"));
const ContractView = lazy(() => import("./pages/contract/index"));
const ContractSetting = lazy(() => import("./pages/contract/update"));
const ContractHistory = lazy(() => import("./pages/contract/history"));
const ClaimCertWorkflow = lazy(
  () => import("./pages/workflow/claimCertWorkflow"),
);

const Error401 = lazy(() => import("./pages/errorPage/401"));
const Error401NoAcc = lazy(() => import("./pages/errorPage/401NoAcc"));
const Error403 = lazy(() => import("./pages/errorPage/403LinkExpired"));
const Error404 = lazy(() => import("./pages/errorPage/404"));
const Error404ClaimRevert = lazy(
  () => import("./pages/errorPage/404ClaimRevert"),
);
const IntegrationError = lazy(() => import("./pages/integrationError"));
const SuperAdmin = lazy(() => import("./pages/superAdmin/admin"));

const routes = [
  {
    path: "/dashboard",
    navText: "dashboard",
    component: Dashboard,
  },
  {
    path: "/contacts",
    navText: "contacts",
    component: Contacts,
    routes: [
      {
        path: "add",
        component: CreateContact,
        auth: (profile) => profile.checkPermission(CONTACT, CREATE),
      },
      {
        path: "edit/:id",
        component: UpdateContact,
        auth: (profile) => profile.checkPermission(CONTACT, EDIT),
      },
    ],
  },
  {
    path: "/notice",
    navText: "dashboard",
    component: Notice,
  },
  {
    path: "/projects",
    navText: "projects",
    component: Projects,
    routes: [{ path: ":projectType?", component: Projects }],
  },
  {
    path: "/claim-cert",
    navText: "claim-cert",
    component: ClaimCert,
  },
  {
    path: "/invitation",
    navText: "projects",
    component: Redirect,
    props: { to: "/" },
    routes: [
      { path: "cert/:code", component: InvitationInfo },
      { path: "subcontractor/:code", component: InvitationInfo },
    ],
  },
  {
    path: "/project",
    navText: "projects",
    component: NewProject,
    auth: (profile) => profile.checkPermission(PROJECT, CREATE),
    routes: [
      {
        path: "view/:projectId",
        component: ProjectInfo,
        auth: (profile) => profile.isFullPermission(),
      },
      {
        path: "view/:projectId/settings",
        component: ProjectSetting,
        auth: (profile, props) =>
          profile.refreshCheckPermission(
            PROJECT,
            EDIT,
            props.match.params.projectId,
          ),
      },
      {
        path: "view/:projectId/setting/workflow/:flowType",
        component: WorkflowSetting,
        auth: (profile, props) =>
          profile.refreshCheckPermission(
            PROJECT,
            EDIT,
            props.match.params.projectId,
          ),
      },
    ],
  },
  // old routes, to delete in future
  {
    path: "/create-new-contract/:proId/:type",
    navText: "projects",
    component: NewContract,
    routes: [
      { path: ":contractId", component: ContractView },
      { path: ":contractId/workflow", component: WorkflowSetting },
      { path: ":contractId/baseContract", component: BaseContract },
      { path: ":contractId/materials", component: Materials },
      { path: ":contractId/variations", component: Variations },
    ],
  },
  // old routes, to delete in future
  {
    path: "/contract/:proId/:type",
    navText: "projects",
    component: NewContract,
    routes: [
      { path: "view/:contractId", component: ContractView },
      { path: "view/:contractId/settings", component: ContractSetting },
    ],
  },
  // new added
  {
    path: "/contract/:proId/:type",
    navText: "projects",
    component: NewContract,
    auth: (profile, props) =>
      profile.refreshCheckPermission(PROJECT, EDIT, props.match.params.proId),
    routes: [
      {
        path: ":contractId",
        component: ContractView,
        auth: (profile) => profile.isFullPermission(),
      },
      {
        path: ":contractId/history",
        component: ContractHistory,
        auth: (profile) => profile.isFullPermission(),
      },
      {
        path: ":contractId/settings",
        component: ContractSetting,
        auth: (profile, props) =>
          profile.refreshCheckPermission(
            CONTRACT,
            EDIT,
            props.match.params.proId,
            props.match.params.contractId,
          ),
      },
      {
        path: ":contractId/workflow",
        component: WorkflowSetting,
        auth: (profile, props) =>
          profile.refreshCheckPermission(
            CONTRACT,
            EDIT,
            props.match.params.proId,
            props.match.params.contractId,
          ),
      },
      {
        path: ":contractId/baseContract",
        component: BaseContract,
        auth: (profile, props) =>
          profile.refreshCheckPermission(
            CONTRACT,
            EDIT,
            props.match.params.proId,
            props.match.params.contractId,
          ),
      },
      {
        path: ":contractId/materials",
        component: Materials,
        auth: (profile, props) =>
          profile.refreshCheckPermission(
            CONTRACT,
            EDIT,
            props.match.params.proId,
            props.match.params.contractId,
          ),
      },
      {
        path: ":contractId/variations",
        component: Variations,
        auth: (profile, props) =>
          profile.refreshCheckPermission(
            CONTRACT,
            EDIT,
            props.match.params.proId,
            props.match.params.contractId,
          ),
      },
    ],
  },
  // old routes, to delete in future
  {
    path: "/make-claim/:contractType/:proId/:contractId",
    navText: "projects",
    component: MakeAClaim,
    routes: [
      { path: ":claimId?", component: MakeAClaim },
      { path: ":claimId/review", component: ClaimOverview },
    ],
  },
  // old routes, to delete in future
  {
    path: "/review-claim/:contractType/:proId/:contractId/:claimId",
    navText: "projects",
    component: ClaimOverview,
    routes: [{ path: "approved", component: ClaimOverview }],
  },
  // old routes, to delete in future
  {
    path: "/make-cert/:contractType/:proId/:contractId/:claimId",
    navText: "projects",
    component: MakeACert,
    routes: [
      { path: ":certId?", component: MakeACert },
      { path: ":certId/review", component: CertOverview },
    ],
  },
  // old routes, to delete in future
  {
    path: "/review-cert/:contractType/:proId/:contractId/:claimId/:certId/approved",
    navText: "projects",
    component: CertOverview,
  },
  {
    path: "/project/:proId/contract/:contractId/:contractType/claim",
    navText: "projects",
    component: MakeAClaim,
    routes: [
      { path: ":claimId?", component: MakeAClaim },
      { path: ":claimId/review", component: ClaimOverview },
      { path: ":claimId/cert", component: MakeACert },
      { path: ":claimId/cert/review", component: CertOverview },
      { path: ":claimId/workflow/:certId?", component: ClaimCertWorkflow },
    ],
  },
  {
    path: "/manage-account",
    navText: "manage-account",
    component: Account,
    auth: (profile) => profile.checkPermission(ACCOUNT, ACCESS),
    routes: [
      { path: "view", component: Account },
      { path: "edit", component: ManageAccount },

      { path: "users", component: ManageUsers },
      { path: "users/add", component: AddUsers },

      { path: "xero-settings", component: XeroSettings },
      {
        path: "profile",
        component: UserDetail,
        auth: (profile) => profile.isFullPermission(),
      },
      {
        path: "profile/edit",
        component: EditProfileDetail,
        auth: (profile) => profile.isFullPermission(),
      },

      {
        path: "plan-payment/:status?",
        component: ManagePlan,
        auth: (profile) => profile.checkPermission(ACCOUNT, EDIT),
      },
      {
        path: "plan-price",
        component: PlanPrice,
        auth: (profile) => profile.checkPermission(ACCOUNT, EDIT),
      },

      { path: "email-summaries", component: EmailSummary },

      { path: "role", component: RoleSelection },
      { path: "integration-error", component: IntegrationError },
    ],
  },
  {
    path: "/admin/accounts",
    component: SuperAdmin,
  },
  {
    path: "/error_401",
    component: Error401,
  },
  {
    path: "/error_401_no_accounts",
    component: Error401NoAcc,
  },
  {
    path: "/error_403_link_expired",
    component: Error403,
  },
  {
    path: "/error_404",
    component: Error404,
  },
  {
    path: "/error_404ClaimRevert",
    component: Error404ClaimRevert,
  },
];

function* flatten(routes, root) {
  for (var route of routes) {
    var current = {
      ...root,
      ...route,
      path: (root ? root.path : "") + route.path + "/",
    };

    yield current;

    if (route.routes) {
      yield* flatten(route.routes, current);
    }
  }
}

export default Array.from(flatten(routes, null));
